import startOfDay from 'date-fns/startOfDay'
import format from 'date-fns/format'
import addDays from 'date-fns/addDays'
import addHours from 'date-fns/addHours'
import {
  dateFormats,
  minimalTimezoneSet,
  timeOfDayDescription,
} from 'constants/common'
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
import {
  callCenterSchedule,
  FILTER_KEY_MAPPING,
  RETURN_FILTER_KEY_MAPPING,
  SECONDARY_FILTERS,
} from './constants'
import { getCurrentZonnedTime } from 'utils/common'
import _groupBy from 'lodash/groupBy'
import _sortBy from 'lodash/sortBy'
import _pick from 'lodash/pick'
import _pickBy from 'lodash/pickBy'
import isEmpty from 'lodash/isEmpty'
import { ParsedUrlQuery } from 'querystring'
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import { getDay, set } from 'date-fns'
import qs from 'qs'
import differenceInHours from 'date-fns/differenceInHours'
import {
  appointmentModeValueMapping,
  CounselingTypeEnum,
} from 'app/Bookings/constants'
import { supportDashboardToProviderSearchKeys } from 'constants/common'
import subMinutes from 'date-fns/subMinutes'
import { providerFilterKeyMapping } from 'dataSource/ProviderSearch'

/**
 * Calculates and returns the max of dates from slots
 * If the slots is less then 3 then it will return 3
 */

const getMaxLengthFromSlots = (slots: DateSlot) => {
  let maxLength = 0

  for (const slotKey in slots) {
    const slot = slots[slotKey]
    maxLength = slot && slot.length > maxLength ? slot.length : maxLength
  }

  return maxLength > 3 ? maxLength : 3
}

/**
 * Returns a array of dates that is generated from the start date till the number limit
 */
export const generateDates = (
  startSlotDate: Date,
  slotListLimit: number
): Date[] => {
  let date: Date = new Date(startSlotDate)
  const endSlotDate: Date = addDays(startSlotDate, slotListLimit)
  const dates: Date[] = []

  while (date < endSlotDate) {
    dates.push(date)
    date = addDays(date, 1)
  }

  return dates
}

/**
 * Generates a new slot mapping according to the dates provided
 * it would fill the missing slots with null till the max length
 */
export const generateDatedSlots = (
  dates: Date[],
  dateSlots: DateSlot,
  validateSlotCondition = (slot: ProviderSlot) => slot
): DateSlot => {
  const generateDatedSlots: DateSlot = {}

  const maxLength = getMaxLengthFromSlots(dateSlots)

  dates.map((date) => {
    // The dates being passed to this function already has the timezone calculation, so not required to parse and calculate timezone
    const formattedDate = format(date, dateFormats.YEAR_MONTH_DAY_FORMAT)
    const slots = !!dateSlots ? dateSlots[formattedDate] : []
    const returnedSlots = []

    for (let i = 0; i < maxLength; i++) {
      if (slots?.[i]) {
        let updatedSlot = slots[i]

        if (!!validateSlotCondition) {
          updatedSlot = validateSlotCondition(slots[i])
        }

        if (updatedSlot) {
          returnedSlots.push(updatedSlot)
        }

        continue
      }

      returnedSlots.push(null)
    }

    generateDatedSlots[formattedDate] = returnedSlots.sort((slotA, slotB) => {
      if (slotA && slotB) {
        return new Date(slotA).getTime() - new Date(slotB).getTime()
      } else if (slotA) {
        return -1
      }

      return 0
    })
  })

  return generateDatedSlots
}

export const isSlotWithin24Hrs = (slotStartTime: string | number): boolean =>
  slotStartTime && differenceInHours(new Date(slotStartTime), new Date()) < 24

const withCounselingTypePrefiltered = (providerDetails, serviceTypeFilters) => {
  // For provider search page
  if (!isEmpty(providerDetails.counselingTypeIds)) {
    const medicationFilter = serviceTypeFilters.find(
      (serviceTypeFilter) =>
        serviceTypeFilter.value === CounselingTypeEnum.MEDICATION
    )
    return !!providerDetails.counselingTypeIds.find(
      (counselingTypeId) => counselingTypeId === medicationFilter?.id
    )
  }

  return false
}

const withoutCounselingTypePrefiltered = (providerDetails) => {
  if (!isEmpty(providerDetails.filters)) {
    // For providers profile and also used in booking flow
    return !!providerDetails.filters.find(
      (filter) =>
        filter.type === ProviderFilterTypes.COUNSELING_TYPE &&
        filter.value === CounselingTypeEnum.MEDICATION
    )
  }

  return false
}

// to be consolidated with isValidMedicationSlotWithProviderFilter
// and deleted
export const isValidMedicationSlotWithProviderId = (
  providerDetails: Provider,
  slot: ProviderSlot
): boolean => {
  let doesProviderSupportMedication = false

  if (!isEmpty(providerDetails.filters)) {
    // For providers profile and also used in booking flow
    doesProviderSupportMedication = !!providerDetails.filters.find(
      (filter) =>
        filter.type === ProviderFilterTypes.COUNSELING_TYPE &&
        filter.value === CounselingTypeEnum.MEDICATION
    )
  }

  if (doesProviderSupportMedication) {
    return !isSlotWithin24Hrs(slot?.start)
  }

  return true
}

export const isValidMedicationSlotWithProviderFilter = (
  providerDetails: Provider,
  slot: ProviderSlot,
  serviceTypeFilters: ProviderFilter[]
): boolean => {
  let doesProviderSupportMedication = false

  if (serviceTypeFilters !== undefined) {
    doesProviderSupportMedication = withCounselingTypePrefiltered(
      providerDetails,
      serviceTypeFilters
    )
  } else {
    doesProviderSupportMedication = withoutCounselingTypePrefiltered(
      providerDetails
    )
  }

  if (doesProviderSupportMedication) {
    return !isSlotWithin24Hrs(slot?.start)
  }

  return true
}

const hardCodedUpdatedFilter = (filter: ProviderFilter): ProviderFilter => {
  let updatedFilter = filter

  // Add any list of hard coded filter condition here
  if (
    filter.type === ProviderFilterTypes.GENDER &&
    filter.value === 'Other/choose not to respond'
  ) {
    updatedFilter = {
      ...filter,
      displayValue: 'Other',
    }
  } else if (filter.type === ProviderFilterTypes.CLIENT_AGE_GROUP) {
    // AJ booking fixes, remove age description
    updatedFilter = {
      ...filter,
      value: `${filter.value} years old`,
      description: '',
    }
  } else if (filter.type === ProviderFilterTypes.TIME_OF_DAY) {
    updatedFilter = {
      ...filter,
      id: filter.value,
      value: filter.description,
      description: timeOfDayDescription[filter.value],
    }
  }

  const { type } = updatedFilter
  // additional is group that is not available in ProviderFilterTypes
  const filterType = [...Object.values(ProviderFilterTypes)].includes(
    type as ProviderFilterTypes
  )
    ? type
    : 'additional'
  return {
    ...updatedFilter,
    type: filterType,
  }
}

/**
 * Returns a grouped array of filter type from a single filter array
 */
export const groupFilters = (
  filters: ProviderFilter[]
): GroupedProviderFilter => {
  const filterGroup: GroupedProviderFilter = _groupBy(
    filters.map(hardCodedUpdatedFilter),
    (filter) => filter.type
  )

  const sortedFilters: GroupedProviderFilter = {}

  // Sorting the grouped values if its required in some order
  for (const [filterType, filterValues] of Object.entries(filterGroup)) {
    let sortedValues

    if (filterType === ProviderFilterTypes.CLIENT_AGE_GROUP) {
      sortedValues = _sortBy(filterValues, [(a) => parseInt(a.value)])
    } else if (filterType === ProviderFilterTypes.GENDER) {
      // The default order of gender values has "Other" at the last index which is what is required. so won't be sorting gender
      // conversation on the decision https://thriveworks.atlassian.net/browse/THRIV-4180?focusedCommentId=26417
      sortedValues = filterValues
    } else if (filterType === ProviderFilterTypes.TIME_OF_DAY) {
      const order = ['early_morning', 'morning', 'afternoon', 'evening']
      sortedValues = _sortBy(filterValues, (item) => order.indexOf(item.id))
    } else {
      sortedValues = _sortBy(filterValues, ['value'])
    }

    sortedFilters[filterType] = sortedValues
  }

  return sortedFilters
}

export interface ParsedFilterType {
  availabilityType?: string[]
  areaOfConcern?: string[]
  clientAgeGroup?: string[]
  gender?: string[]
  counselingApproach?: string[]
  language?: string[]
  timeOfDay?: string[]
}

export const getFilterLookup = (
  filters: ProviderFilter[]
): ProviderFilterLookup =>
  _groupBy<ProviderFilter>(filters, (filter) => filter.id)

/**
 * Format selected grouped filter as required by API
 */
export const formatSelectedFilters = (
  groupedFilters: GroupedProviderFilter,
  filterKeys = RETURN_FILTER_KEY_MAPPING
): ParsedFilterType => {
  const parsedFilter = {}

  Object.keys(groupedFilters).map((groupedFilterKey) => {
    parsedFilter[filterKeys[groupedFilterKey]] = groupedFilters[
      groupedFilterKey
    ].map((filter) => filter?.id)
  })

  return parsedFilter
}

/**
 * Format selected grouped filter as required by API (For Split new search only)
 */
export const formatSelectedFiltersWithCounselingType = (
  groupedFilters: GroupedProviderFilter
): ParsedFilterType => {
  const returnFilterKeyMappingWithCounselingType = {
    ...RETURN_FILTER_KEY_MAPPING,
    counseling_type: 'counselingType',
  }

  return formatSelectedFilters(
    groupedFilters,
    returnFilterKeyMappingWithCounselingType
  )
}

export const getGenderValuesFromIds = (
  genderIds: string[],
  groupedGender: ProviderFilter[]
): string[] | null => {
  if (genderIds) {
    return groupedGender
      .filter((filter) => genderIds.includes(filter.id))
      .map((filter) => filter.value)
  }

  return null
}

/**
 * get selected GroupedProvider based on query params
 */
export const getSelectedFiltersFromQueryParams = (
  groupedFilters: GroupedProviderFilter,
  query: NodeJS.Dict<string | string[]>,
  filterKeys = RETURN_FILTER_KEY_MAPPING
): GroupedProviderFilter => {
  const selectedFilter: GroupedProviderFilter = {}

  // finds the id from groupedFilters and creates a object for selected filters
  Object.keys(query).map((queryKey) => {
    const groupedFilterKey = queryKey.replace('[]', '')

    if (Object.values(filterKeys).includes(groupedFilterKey)) {
      if (Array.isArray(query[queryKey])) {
        const queryValue = query[queryKey] as string[]
        selectedFilter[
          FILTER_KEY_MAPPING[groupedFilterKey]
        ] = queryValue.map((queryFilterId) =>
          groupedFilters[FILTER_KEY_MAPPING[groupedFilterKey]].find(
            (filter: ProviderFilter) => filter.id === queryFilterId
          )
        )
      } else {
        // When there is single filter param it parses as a string not as a array
        const singleQueryFilter = groupedFilters[
          FILTER_KEY_MAPPING[groupedFilterKey]
        ]?.find((filter: ProviderFilter) => filter.id === query[queryKey])
        selectedFilter[FILTER_KEY_MAPPING[groupedFilterKey]] = [
          singleQueryFilter,
        ]
      }
    }
  })

  return selectedFilter
}

/**
 * get selected GroupedProvider based on query params (For Split new search only)
 */
export const getSelectedFiltersWithCounselingTypeFromQueryParams = (
  groupedFilters: GroupedProviderFilter,
  query: NodeJS.Dict<string | string[]>
): GroupedProviderFilter => {
  const returnFilterKeyMappingWithCounselingType = {
    ...RETURN_FILTER_KEY_MAPPING,
    counseling_type: 'counselingType',
  }

  return getSelectedFiltersFromQueryParams(
    groupedFilters,
    query,
    returnFilterKeyMappingWithCounselingType
  )
}

export const formatPracticesFromProviders = (
  providers: Provider[] = [],
  initialPractices: PracticeProvider[] = []
): PracticeProvider[] => {
  const practices = initialPractices
  providers.map((provider) => {
    const practice = provider?.practice
    const index = practices.findIndex(
      (existingPractice) => existingPractice.id === practice.id
    )

    if (index !== -1) {
      practices[index].providers.push(provider)
    } else {
      practices.push({
        ...practice,
        providers: [provider],
      })
    }
  })
  return practices
}

//Check if only all Providers Filter exists in the query params
export const isOnlyAllProviderFilter = (queryObj: qs.ParsedQs): boolean => {
  const validKeys = [
    'state',
    'latitude',
    'longitude',
    'location',
    'limit',
    'offset',
    'slotStartDate',
    'slotEndDate',
    'distanceInMiles',
    ...supportDashboardToProviderSearchKeys,
  ]

  const isAllProviderFilter = Object.keys(queryObj).every((queryParam) =>
    validKeys.includes(queryParam)
  )
  return isAllProviderFilter
}

/**
 * @description
 * creates formattedTimeZoneSet which is a list of timezones converted to an option list which could be accepted by the Select Component
 * @param minimalTimezoneSet list of the formatted timezones we support.
 * @returns {TimezoneSelectItem} the formatted list created from {minimalTimezoneSet} and adds the current timezone if the current timezone is not present in minimalTimezoneSet.
 */
export const getFormattedListTimeZones = (): TimezoneSelectItem[] => {
  const currentTimeZone: string = Intl.DateTimeFormat().resolvedOptions()
    .timeZone
  const formattedTimeZoneSet = minimalTimezoneSet.map(
    ({ label, tzCode, tzName, tzAbbr }) => ({
      label,
      value: tzName,
      tzCode,
      tzAbbr,
    })
  )

  if (minimalTimezoneSet.find(({ tzCode }) => tzCode === currentTimeZone)) {
    return formattedTimeZoneSet
  }

  return [
    ...formattedTimeZoneSet,
    {
      label: currentTimeZone,
      value: currentTimeZone,
      tzCode: currentTimeZone,
      tzAbbr: currentTimeZone,
    },
  ]
}

export const matchTimezoneFromList = (
  tzName: string,
  tzCode: string
): TimeZone => {
  let matchingTimezone
  const timeZoneFromList = minimalTimezoneSet.filter(
    (timeZoneItem) => timeZoneItem.tzName === tzName
  )

  if (timeZoneFromList?.length > 1) {
    matchingTimezone = timeZoneFromList.find(
      (timezone) => timezone.tzCode === tzCode
    )
  } else if (timeZoneFromList?.length === 1) {
    matchingTimezone = timeZoneFromList[0]
  }

  return matchingTimezone
}

/**
 *default parameter selects the browser timeZone
 */
export const getTimeZoneFromFormattedList = (
  currentTimeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
  tzCode: string
): TimezoneSelectItem => {
  const matchingTimezone = matchTimezoneFromList(currentTimeZone, tzCode)

  if (matchingTimezone) {
    return {
      label: matchingTimezone.label,
      value: matchingTimezone.tzName,
      tzCode: matchingTimezone.tzCode,
      tzAbbr: matchingTimezone.tzAbbr,
    }
  }

  return {
    label: currentTimeZone,
    value: currentTimeZone,
    tzCode: currentTimeZone,
    tzAbbr: currentTimeZone,
  }
}

export const getUserTimeZoneName = (): string => {
  return Intl.DateTimeFormat(undefined, { timeZoneName: 'long' })
    .formatToParts(new Date())
    .find((part) => part.type === 'timeZoneName').value
}

/**
 *  checks if the selected date is within the week of the selected start date
 */
export const isSelectedSlotInRange = (
  selectedSlot: SelectedSlot,
  timeZone: string,
  startSlotDate: Date,
  slotListLimit: number
): boolean => {
  if (selectedSlot.slot?.start) {
    const currentSelectedSlot = utcToZonedTime(
      selectedSlot.slot.start,
      timeZone
    )
    const slotsStartDate = new Date(startSlotDate)
    slotsStartDate.setHours(0, 0, 0, 0)
    const slotsEndDate = new Date(slotsStartDate)
    slotsEndDate.setDate(slotsEndDate.getDate() + slotListLimit)

    const isSlotInRange =
      currentSelectedSlot >= slotsStartDate &&
      currentSelectedSlot <= slotsEndDate
    return isSlotInRange
  }
}

/**
 * Used in List card
 */
export const getBookingQueryObject = (
  queryObject: ParsedUrlQuery,
  slotId: string,
  timeZone: string
): unknown => {
  const {
    insuranceId,
    insurancePlan,
    selfPay,
    userId,
    patients,
    leadId,
  } = queryObject

  return {
    tz: timeZone,
    ...(insuranceId && { insuranceId }),
    ...(insurancePlan && { insurancePlan }),
    ...(selfPay && { selfPay }),
    ...(userId && { userId }),
    ...(!!patients && { patients }),
    ...(!!leadId && { leadId: leadId }),
    slotId,
  }
}

/**
 * returns the browser timezone date from a zoned timezone date
 */
export const getBrowserTimeFromZonedTime = (
  zonedTime: Date,
  timeZone: string
): Date => {
  const utcTime = zonedTimeToUtc(zonedTime, timeZone)
  const browserTime = utcToZonedTime(
    utcTime,
    Intl.DateTimeFormat().resolvedOptions().timeZone
  )

  return browserTime
}

/**
 * This function is used in
 * - Provider slot hook for slots api
 * - Provider listing page for providers api
 *
 * Calculation logic start date :-
 *    1. if todays date is selected then add 4 hrs from the current time (bussiness requirement - don't want to show slots between now and 4 hours from now)
 *    2. if any other date is selected then the time of the date will start from the start of the date eg: 21-2-2021T00:00:00 (setting of the time to 00:00:00 is done on the component level not here)
 *
 * Calculation logic end date :-
 *    1. slotListLimit will the number of days that needs to be added to the start date the resulting date will be the end date
 *    2. set the time of the end date to 00:00:00 (start of the day) to fetch exactly till the end date slots
 */
export const getSlotsStartEndDate = (
  startSlotDate: Date,
  slotListLimit: number,
  timeZone: string
): SlotsRange => {
  let modifiedStartSlotDate = startSlotDate
  const exactModifiedEndSlotDate = startOfDay(
    addDays(startSlotDate, slotListLimit)
  )
  const modifiedEndSlotDate = subMinutes(exactModifiedEndSlotDate, 1)
  const currentZonnedTime = getCurrentZonnedTime(timeZone)

  // If the selected start date is between now and 4 hours from now, if so then select the 4 hour from now time
  // because we want to show only the appointment 4 hours from now
  if (startSlotDate <= addHours(currentZonnedTime, 4)) {
    modifiedStartSlotDate = addHours(currentZonnedTime, 4)
  }

  const browserStartTime = getBrowserTimeFromZonedTime(
    modifiedStartSlotDate,
    timeZone
  )
  const browserEndTime = getBrowserTimeFromZonedTime(
    modifiedEndSlotDate,
    timeZone
  )

  return {
    startDate: browserStartTime,
    endDate: browserEndTime,
  }
}

export const getLabelForFilterItem = (
  filterItem: ProviderFilter,
  showDescriptionInLabel: boolean
): string => {
  let description = filterItem.description || ''

  if (!showDescriptionInLabel) {
    description = ''
  }

  return `${filterItem.displayValue || filterItem.value} ${description}`
}

export const getTextFromHtml = (text: string): string => {
  // This regex targets all the tags in a string and the replace removes those tags.
  const parsedText = text.replace(/<br>(?=(?:\s*<[^>]>)$)|(<br>)|<[^>]*>/gi, '')
  return parsedText.replace(/&nbsp;/g, ' ')
}

export const formatAvailabilityTypeValues = (
  availabilityFilters: ProviderFilter[]
): ProviderFilter[] => {
  return availabilityFilters.map((filter) => ({
    ...filter,
    value: appointmentModeValueMapping[filter.value],
  }))
}

export const mapFilterIDsWithFilterLookup = (
  providerData: ListCardProvider,
  filterPropertyNames: string[],
  filterLookupData: ProviderFilterLookup
): ProviderFilter[] => {
  const filtersData = filterPropertyNames.reduce((allFilters, currPropName) => {
    const filterIds = providerData[currPropName]
    const filterData =
      filterIds?.reduce(
        (filters, id) => [...filters, filterLookupData[id]?.[0] || []],
        []
      ) || []
    return allFilters.concat(filterData)
  }, [])

  return filtersData
}

export const getFilterIdsByValue = (
  groupedFilters: GroupedProviderFilter,
  filterType: string,
  filterValues: string[]
): string[] => {
  const filterIds = []

  groupedFilters[filterType].forEach((filter: ProviderFilter) => {
    if (filterValues.includes(filter.value)) {
      filterIds.push(filter.id)
    }
  })

  return filterIds
}

export const slugifyCity = (city: string): string => {
  return city.split(' ').join('-').toLowerCase()
}

export const getFilterValueFromParsedURL = (
  groupedFilters: GroupedProviderFilter,
  query: ParsedUrlQuery
): { [key: string]: string | string[] | number } | null => {
  const trackerInformation = {}

  Object.keys(providerFilterKeyMapping).forEach((providerFilterKey) => {
    const formattedFilterKey = providerFilterKey.replace('[]', '')
    const queryValue = query[formattedFilterKey]

    if (queryValue) {
      const filterKeyMapped = FILTER_KEY_MAPPING[formattedFilterKey]
      const providerSection = groupedFilters[filterKeyMapped]

      if (providerSection) {
        const selectedValues = providerSection
          .filter((filter) => query[formattedFilterKey].includes(filter.id))
          .map((filter) => filter.value)

        trackerInformation[filterKeyMapped] = selectedValues
      }
    }

    return null
  })

  return trackerInformation
}

export const formatSupplyReserveParams = (
  intakeSupplyReserveData: Partial<IntakeSupplyReserveDetails>,
  userType = ''
): { [key: string]: string | boolean | number } => {
  const {
    stateCode,
    shouldReserveIntakeSupply,
    partnershipCode,
    businessPartnerTierName,
    businessPartnerTierThreshold,
    totalAvailableIntakes,
  } = intakeSupplyReserveData ?? {}
  return {
    state_code: stateCode,
    partnership_code: partnershipCode || '',
    business_partner_tier_name: businessPartnerTierName || '',
    business_partner_tier_threshold: businessPartnerTierThreshold || '',
    total_available_intakes: totalAvailableIntakes || '',
    should_reserve_intake_supply: shouldReserveIntakeSupply,
    user_type: userType,
  }
}

export const hasSecondaryFilters = (
  queryParamObject: Record<string, unknown>
): boolean => {
  const secondaryFitlers = _pick(queryParamObject, ...SECONDARY_FILTERS)
  const notNullSecondaryFilter = _pickBy(secondaryFitlers, (value) => value)
  const notNullSecondaryFilterKeys = Object.keys(notNullSecondaryFilter)

  return notNullSecondaryFilterKeys.length > 0
}

export const getNextEventTimeInLocal = (
  currentETTime: Date,
  currentDayNum: number,
  targetDay: number,
  totalMinutes: number,
  timeZone: string
): string => {
  const eventDate = addDays(currentETTime, (targetDay - currentDayNum + 7) % 7)
  const eventTimeInET = set(eventDate, {
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
    seconds: 0,
  })

  const localDate = getBrowserTimeFromZonedTime(eventTimeInET, timeZone)
  return format(localDate, 'h:mma').replace(':00', '') // 7:00AM becomes 7AM
}

export const getCallCenterStatus = (): {
  isCallCenterStatusOpen: boolean
  nextEvent: string
} => {
  const timeZone = 'America/New_York'

  // Get the current time in Eastern Time Zone
  const now = new Date()
  const currentETTime = utcToZonedTime(now, timeZone)
  const currentDayNum = getDay(currentETTime)

  const currentTimeInMinutes =
    currentETTime.getHours() * 60 + currentETTime.getMinutes()

  // Get today's schedule
  const {
    open: todayOpeningTimeInMins,
    close: todayClosingTimeInMins,
  } = callCenterSchedule[currentDayNum]

  if (
    currentTimeInMinutes >= todayOpeningTimeInMins &&
    currentTimeInMinutes < todayClosingTimeInMins
  ) {
    // Call center is open, next event is today's closing time
    const nextEvent = getNextEventTimeInLocal(
      currentETTime,
      currentDayNum,
      currentDayNum,
      todayClosingTimeInMins,
      timeZone
    )

    return {
      isCallCenterStatusOpen: true,
      nextEvent,
    }
  }

  // Call center is closed, check if we're before today's opening or need to move to the next day
  const nextDayNum =
    currentTimeInMinutes < todayOpeningTimeInMins
      ? currentDayNum
      : (currentDayNum + 1) % 7

  const { open: nextDayOpeningInMins } = callCenterSchedule[nextDayNum]
  const nextEvent = getNextEventTimeInLocal(
    currentETTime,
    currentDayNum,
    nextDayNum,
    nextDayOpeningInMins,
    timeZone
  )

  return {
    isCallCenterStatusOpen: false,
    nextEvent,
  }
}
